import type {
  FormFieldDisplayOverride,
  FormWidgetConfig,
  FormWidgetCountry,
  FormWidgetVariant,
} from './form-widget.types';
import { IDENTIFICATION_CONFIG } from './identification';
import {
  CITY_OPTIONS_BY_REGION,
  REGION_OPTIONS_BY_COUNTRY,
  SALARY_OPTIONS_BY_COUNTRY,
} from './regions';

const PRIVACY = {
  label: 'Acepto los',
  linkLabel: 'términos y condiciones',
};

const COUNTRY_OPTIONS = [
  { label: 'Chile', value: 'CL' },
  { label: 'México', value: 'MX' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Argentina', value: 'AR' },
  { label: 'Otro', value: 'OTHER' },
];

export const SUPPORTED_FORM_COUNTRIES = [
  'AR',
  'CL',
  'MX',
  'CO',
] as const satisfies readonly FormWidgetCountry[];

const FORM_COUNTRY_LABELS: Record<FormWidgetCountry, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  MX: 'México',
  CO: 'Colombia',
};

const EDUCATION_LEVEL_OPTIONS = [
  { label: 'Profesional Egresado', value: 'Profesional Egresado' },
  { label: 'Estudiante de Pregrado', value: 'Estudiante de Pregrado' },
  { label: 'Otro', value: 'Otro' },
];

const EDUCATION_LEVELS_WITH_CAREER = ['Profesional Egresado', 'Estudiante de Pregrado'];

const CAREER_OPTIONS = [
  { label: 'Psicología', value: 'Psicología' },
  { label: 'Terapia Ocupacional', value: 'Terapia Ocupacional' },
  { label: 'Trabajo social', value: 'Trabajo social' },
  { label: 'Educación y Pedagogía', value: 'Educación y Pedagogía' },
  { label: 'Administración pública', value: 'Administración pública' },
  { label: 'Ciencias Sociales', value: 'Ciencias Sociales' },
  { label: 'Derecho y/o Ciencias Jurídicas', value: 'Derecho y/o Ciencias Jurídicas' },
  { label: 'Enfermería', value: 'Enfermería' },
  { label: 'Fonoaudiología', value: 'Fonoaudiología' },
  { label: 'Kinesiología', value: 'Kinesiología' },
  { label: 'Medicina', value: 'Medicina' },
  { label: 'Nutrición', value: 'Nutrición' },
  { label: 'Obstetricia y Puericultura', value: 'Obstetricia y Puericultura' },
  { label: 'Sociología', value: 'Sociología' },
  { label: 'Otra', value: 'Otra' },
];

const toOptions = (labels: string[]) => labels.map((label) => ({ label, value: label }));

const PSYCHOLOGY_AREAS = toOptions([
  'Psicología Clínica',
  'Psicología Educacional',
  'Psicología Organizacional',
  'Psicología Deportiva',
  'Psicología Forense / Perito',
  'Psicología Comunitaria',
  'Psicología Laboral',
  'Otro',
]);
const EDUCATION_AREAS = toOptions([
  'Educación Diferencial',
  'Educación de Párvulos',
  'Psicopedagogía',
  'Educación social',
  'Pedagogía',
  'Otro',
]);
const MEDICINE_AREAS = toOptions([
  'Medicina General',
  'Neurología',
  'Psiquiatría',
  'Nutriología',
  'Ginecología',
  'Pediatría',
  'Otro',
]);

const PRACTICE_AREA_OPTIONS: Record<string, { label: string; value: string }[]> = {
  'Profesional Egresado|Psicología': PSYCHOLOGY_AREAS,
  'Profesional Egresado|Educación y Pedagogía': EDUCATION_AREAS,
  'Profesional Egresado|Medicina': MEDICINE_AREAS,
  'Estudiante de Pregrado|Educación y Pedagogía': EDUCATION_AREAS,
  'Estudiante de Pregrado|Medicina': MEDICINE_AREAS,
};

const practiceAreaField = {
  name: 'practiceArea',
  type: 'select' as const,
  label: 'Área de ejercicio profesional',
  required: true,
  placeholder: 'Selecciona una opción',
  layout: { span: 2 as const },
  optionsWhen: {
    fields: ['educationLevel', 'career'],
    options: PRACTICE_AREA_OPTIONS,
  },
};

export const contactBaseConfig: FormWidgetConfig = {
  formId: 'contact',
  variant: 'contact',
  country: 'CL',
  header: {
    title: 'Contáctanos',
    description: 'Completa el formulario y nos pondremos en contacto contigo.',
    align: 'left',
  },
  fields: [
    {
      name: 'firstname',
      type: 'text',
      label: 'Nombre',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastname',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
    },
    {
      name: 'affair',
      type: 'text',
      label: 'Asunto',
      required: true,
      layout: { span: 2 },
    },
    {
      name: 'message',
      type: 'textarea',
      label: '¿Cuál es tu consulta?',
      required: true,
      layout: { span: 2 },
    },
  ],
  submit: {
    label: 'Enviar datos',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Mensaje enviado!',
      message: 'Recibimos tu mensaje y te responderemos a la brevedad.',
    },
    error: {
      title: 'No pudimos enviar tu mensaje',
      message: 'Intenta nuevamente en unos segundos.',
    },
    validation: {
      phone: 'Ingresa un teléfono válido.',
      checkbox: 'Debes aceptar los términos y condiciones para continuar.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'contact-widget',
  },
  scenarioValues: {
    partial: {
      firstname: 'María',
      email: 'maria@ejemplo.com',
    },
    complete: {
      firstname: 'María',
      lastname: 'García',
      email: 'maria@ejemplo.com',
      phone: '+56912345678',
      affair: 'Consulta general',
      message: 'Mensaje de prueba con suficiente longitud.',
    },
  },
};

export const courseClosedBaseConfig: FormWidgetConfig = {
  formId: 'course-closed',
  variant: 'course-closed',
  country: 'CL',
  header: {
    title: '¡Inscripciones cerradas!',
    description: 'Completa el formulario y sé el primero en enterarte de la nueva fecha de inicio.',
    align: 'left',
  },
  fields: [
    {
      name: 'programName',
      type: 'readonly',
      label: 'Programa',
      defaultValue: 'Programa ADIPA Chile',
      required: true,
      hidden: true,
      layout: { span: 2 },
    },
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo',
      required: true,
      autocomplete: 'email',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
  ],
  submit: {
    label: 'Quiero que me avisen',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Datos recibidos!',
      message: 'Te avisaremos cuando tengamos una nueva fecha disponible.',
    },
    error: {
      title: 'No pudimos enviar tus datos',
      message: 'Intenta nuevamente en unos segundos.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'course-closed-widget',
    programName: 'Programa ADIPA Chile',
  },
  scenarioValues: {
    partial: {
      programName: 'Programa ADIPA Chile',
      firstName: 'María',
      phone: '+56912345678',
    },
    complete: {
      programName: 'Programa ADIPA Chile',
      firstName: 'María',
      lastName: 'García',
      phone: '+56912345678',
      email: 'maria@ejemplo.com',
    },
  },
};

export const priceReminderBaseConfig: FormWidgetConfig = {
  formId: 'price-reminder',
  variant: 'price-reminder',
  country: 'CL',
  header: {
    title: 'Te avisaremos antes de que suba el precio',
    description: 'Déjanos tus datos y te recordaremos 1 día antes del cambio de etapa.',
    align: 'left',
  },
  fields: [
    {
      name: 'programName',
      type: 'readonly',
      label: 'Programa',
      defaultValue: 'Programa ADIPA Chile',
      required: true,
      hidden: true,
      layout: { span: 2 },
    },
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
  ],
  submit: {
    label: 'Enviar mis datos',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Datos recibidos!',
      message: 'Te avisaremos 1 día antes del cambio de precio. ¡Gracias por confiar en Adipa!',
    },
    error: {
      title: 'No pudimos enviar tus datos',
      message: 'Intenta nuevamente en unos segundos.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'price-reminder-widget',
    programName: 'Programa ADIPA Chile',
  },
  scenarioValues: {
    partial: {
      programName: 'Programa ADIPA Chile',
      firstName: 'María',
      phone: '+56912345678',
    },
    complete: {
      programName: 'Programa ADIPA Chile',
      firstName: 'María',
      lastName: 'García',
      phone: '+56912345678',
      email: 'maria@ejemplo.com',
    },
  },
};

export const seminarRegistrationBaseConfig: FormWidgetConfig = {
  formId: 'seminar-registration',
  variant: 'seminar-registration',
  country: 'CL',
  banner: {
    enabled: true,
    type: 'countdown',
    eyebrow: 'Cupos disponibles',
    title: 'El seminario comienza pronto',
    description: 'Inscríbete y asegura tu acceso al evento.',
    countdownLabel: 'Cierre de inscripción',
    targetLabel: '02 días · 14 horas · 30 min',
  },
  header: {
    title: '¡Inscríbete ahora!',
    align: 'left',
  },
  fields: [
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
      layout: { span: 2 },
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
    {
      name: 'identificationCountry',
      type: 'select',
      label: 'País',
      required: true,
      defaultValue: 'CL',
      options: COUNTRY_OPTIONS,
    },
    {
      name: 'identificationNumber',
      type: 'text',
      label: IDENTIFICATION_CONFIG.CL.label,
      placeholder: IDENTIFICATION_CONFIG.CL.placeholder,
      required: true,
      inputMode: IDENTIFICATION_CONFIG.CL.inputMode,
      layout: { span: 2 },
      displayWhen: {
        field: 'identificationCountry',
        values: {
          CL: {
            label: IDENTIFICATION_CONFIG.CL.label,
            placeholder: IDENTIFICATION_CONFIG.CL.placeholder,
            inputMode: IDENTIFICATION_CONFIG.CL.inputMode,
          },
          MX: {
            label: IDENTIFICATION_CONFIG.MX.label,
            placeholder: IDENTIFICATION_CONFIG.MX.placeholder,
            inputMode: IDENTIFICATION_CONFIG.MX.inputMode,
          },
          CO: {
            label: IDENTIFICATION_CONFIG.CO.label,
            placeholder: IDENTIFICATION_CONFIG.CO.placeholder,
            inputMode: IDENTIFICATION_CONFIG.CO.inputMode,
          },
          AR: {
            label: IDENTIFICATION_CONFIG.AR.label,
            placeholder: IDENTIFICATION_CONFIG.AR.placeholder,
            inputMode: IDENTIFICATION_CONFIG.AR.inputMode,
          },
          OTHER: {
            label: IDENTIFICATION_CONFIG.OTHER.label,
            placeholder: IDENTIFICATION_CONFIG.OTHER.placeholder,
            inputMode: IDENTIFICATION_CONFIG.OTHER.inputMode,
          },
        },
      },
      validation: {
        type: 'identification',
        countryField: 'identificationCountry',
      },
    },
    {
      name: 'educationLevel',
      type: 'select',
      label: 'Nivel de educación',
      required: true,
      placeholder: 'Selecciona una opción',
      options: EDUCATION_LEVEL_OPTIONS,
      layout: { span: 2 },
    },
    {
      name: 'career',
      type: 'select',
      label: 'Carrera',
      required: true,
      placeholder: 'Selecciona una opción',
      options: CAREER_OPTIONS,
      layout: { span: 2 },
      visibleWhen: {
        field: 'educationLevel',
        values: EDUCATION_LEVELS_WITH_CAREER,
      },
    },
    practiceAreaField,
  ],
  submit: {
    label: 'Quiero participar',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Inscripción recibida!',
      message: 'Te enviaremos la información del seminario al correo indicado.',
    },
    error: {
      title: 'No pudimos completar la inscripción',
      message: 'Revisa tu conexión e intenta nuevamente.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'seminar-registration-widget',
  },
  scenarioValues: {
    partial: {
      firstName: 'Camila',
      lastName: 'Muñoz',
      identificationCountry: 'CL',
    },
    complete: {
      firstName: 'Camila',
      lastName: 'Muñoz',
      phone: '+56912345678',
      email: 'camila@ejemplo.com',
      identificationCountry: 'CL',
      identificationNumber: '12.345.678-5',
      educationLevel: 'Profesional Egresado',
      career: 'Psicología',
      practiceArea: 'Psicología Clínica',
    },
  },
};

export const seminarAttendanceBaseConfig: FormWidgetConfig = {
  formId: 'seminar-attendance',
  variant: 'seminar-attendance',
  country: 'CL',
  header: {
    title: 'Completa los datos para registrar tu asistencia.',
    align: 'center',
  },
  fields: [
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
    },
    {
      name: 'identificationCountry',
      type: 'select',
      label: 'País',
      required: true,
      defaultValue: 'CL',
      options: COUNTRY_OPTIONS,
    },
    {
      name: 'identificationNumber',
      type: 'text',
      label: IDENTIFICATION_CONFIG.CL.label,
      placeholder: IDENTIFICATION_CONFIG.CL.placeholder,
      required: true,
      inputMode: IDENTIFICATION_CONFIG.CL.inputMode,
      displayWhen: {
        field: 'identificationCountry',
        values: {
          CL: {
            label: IDENTIFICATION_CONFIG.CL.label,
            placeholder: IDENTIFICATION_CONFIG.CL.placeholder,
            inputMode: IDENTIFICATION_CONFIG.CL.inputMode,
          },
          MX: {
            label: IDENTIFICATION_CONFIG.MX.label,
            placeholder: IDENTIFICATION_CONFIG.MX.placeholder,
            inputMode: IDENTIFICATION_CONFIG.MX.inputMode,
          },
          CO: {
            label: IDENTIFICATION_CONFIG.CO.label,
            placeholder: IDENTIFICATION_CONFIG.CO.placeholder,
            inputMode: IDENTIFICATION_CONFIG.CO.inputMode,
          },
          AR: {
            label: IDENTIFICATION_CONFIG.AR.label,
            placeholder: IDENTIFICATION_CONFIG.AR.placeholder,
            inputMode: IDENTIFICATION_CONFIG.AR.inputMode,
          },
          OTHER: {
            label: IDENTIFICATION_CONFIG.OTHER.label,
            placeholder: IDENTIFICATION_CONFIG.OTHER.placeholder,
            inputMode: IDENTIFICATION_CONFIG.OTHER.inputMode,
          },
        },
      },
      validation: {
        type: 'identification',
        countryField: 'identificationCountry',
      },
    },
  ],
  submit: {
    label: 'Ver certificado',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Asistencia registrada!',
      message: 'Registramos tu asistencia. Te enviaremos tu certificado al correo indicado.',
    },
    error: {
      title: 'No pudimos registrar tu asistencia',
      message: 'Revisa tu conexión e intenta nuevamente.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    label: 'He leído y acepto las',
    linkLabel: 'Políticas de privacidad',
  },
  context: {
    source: 'seminar-attendance-widget',
  },
  scenarioValues: {
    partial: {
      firstName: 'Camila',
      lastName: 'Muñoz',
      identificationCountry: 'CL',
    },
    complete: {
      firstName: 'Camila',
      lastName: 'Muñoz',
      phone: '+56912345678',
      email: 'camila@ejemplo.com',
      identificationCountry: 'CL',
      identificationNumber: '12.345.678-5',
    },
  },
};

export const resourceDownloadBaseConfig: FormWidgetConfig = {
  formId: 'resource-download',
  variant: 'resource-download',
  country: 'CL',
  header: {
    title: 'Completa el formulario para descargar el manual',
    align: 'left',
  },
  fields: [
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
      layout: { span: 2 },
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
    {
      name: 'country',
      type: 'select',
      label: 'País',
      required: true,
      defaultValue: 'CL',
      options: COUNTRY_OPTIONS,
    },
    {
      name: 'educationLevel',
      type: 'select',
      label: 'Nivel de educación',
      required: true,
      placeholder: 'Selecciona una opción',
      options: EDUCATION_LEVEL_OPTIONS,
      layout: { span: 2 },
    },
    {
      name: 'career',
      type: 'select',
      label: 'Carrera',
      required: true,
      placeholder: 'Selecciona una opción',
      options: CAREER_OPTIONS,
      layout: { span: 2 },
      visibleWhen: {
        field: 'educationLevel',
        values: EDUCATION_LEVELS_WITH_CAREER,
      },
    },
    practiceAreaField,
  ],
  submit: {
    label: 'Enviar datos',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Solicitud recibida!',
      message: 'Te enviaremos el manual al correo indicado.',
    },
    error: {
      title: 'No pudimos procesar la descarga',
      message: 'Intenta nuevamente en unos segundos.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'resource-download-widget',
    resourceType: 'ebook',
  },
  scenarioValues: {
    partial: {
      firstName: 'Daniela',
      email: 'daniela@ejemplo.com',
      country: 'CL',
    },
    complete: {
      firstName: 'Daniela',
      lastName: 'Rojas',
      email: 'daniela@ejemplo.com',
      phone: '+56912345678',
      country: 'CL',
      educationLevel: 'Estudiante de Pregrado',
      career: 'Medicina',
      practiceArea: 'Medicina General',
    },
  },
};

export const partnershipBaseConfig: FormWidgetConfig = {
  formId: 'partnership',
  variant: 'partnership',
  country: 'CL',
  header: {
    title: '¿Te gustaría tener un convenio con ADIPA?',
    description:
      'Cuéntanos de tu empresa y nos pondremos en contacto para conversar sobre un futuro convenio.',
    align: 'left',
  },
  fields: [
    {
      name: 'firstname',
      type: 'text',
      label: 'Nombre',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastname',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
    {
      name: 'company',
      type: 'text',
      label: 'Nombre de tu empresa',
      required: true,
      layout: { span: 2 },
    },
    {
      name: 'website',
      type: 'text',
      label: 'Sitio web o red social',
      required: true,
      placeholder: 'https://',
      layout: { span: 2 },
    },
  ],
  submit: {
    label: 'Enviar',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Registro enviado!',
      message:
        'Dentro de los próximos días te contactaremos para conversar sobre un futuro convenio.',
    },
    error: {
      title: 'No pudimos enviar tu registro',
      message: 'Intenta nuevamente en unos segundos.',
    },
    validation: {
      phone: 'Ingresa un teléfono válido.',
      checkbox: 'Debes aceptar los términos y condiciones para continuar.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'partnership-widget',
  },
  scenarioValues: {
    partial: {
      firstname: 'Camila',
      lastname: 'Rojas',
      email: 'camila@empresa.cl',
    },
    complete: {
      firstname: 'Camila',
      lastname: 'Rojas',
      email: 'camila@empresa.cl',
      phone: '+56 9 1234 5678',
      company: 'Empresa Demo',
      website: 'https://empresademo.cl',
    },
  },
};

const INSTITUTIONAL_BUDGET_OPTIONS = [
  { label: '<1.500.000 CLP', value: '<1.500.000 CLP' },
  { label: '1.500.000 a 3.000.000 CLP', value: '1.500.000 a 3.000.000 CLP' },
  { label: '3.000.000 a 5.000.000 CLP', value: '3.000.000 a 5.000.000 CLP' },
  { label: '>5.000.000 CLP', value: '>5.000.000 CLP' },
];

const INSTITUTIONAL_MODALITY_OPTIONS = [
  { label: 'Online en vivo', value: 'Online en vivo' },
  { label: 'Presencial', value: 'Presencial' },
  { label: 'Mixto', value: 'Mixto' },
  { label: 'Otra', value: 'Otra' },
];

const INSTITUTIONAL_HEADCOUNT_OPTIONS = [
  { label: '1 a 10', value: '1_10' },
  { label: '11 a 25', value: '11_25' },
  { label: '26 a 50', value: '26_50' },
  { label: '51 a 100', value: '51_100' },
  { label: 'Más de 100', value: 'mas_de_100' },
];

const INSTITUTIONAL_ONSITE_MODALITIES = ['Presencial'];

export const institutionalTrainingBaseConfig: FormWidgetConfig = {
  formId: 'institutional-training',
  variant: 'institutional-training',
  country: 'CL',
  header: {
    title: 'Completa el formulario',
    description:
      'Cuéntanos qué necesita tu organización y te contactaremos con una propuesta a la medida.',
    align: 'left',
  },
  fields: [
    {
      name: 'firstname',
      type: 'text',
      label: 'Nombre',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastname',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      required: true,
      autocomplete: 'email',
      layout: { span: 2 },
    },
    {
      name: 'company',
      type: 'text',
      label: 'Nombre de la organización',
      required: true,
      autocomplete: 'organization',
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono',
      required: true,
    },
    {
      name: 'tentativeBudgetRange',
      type: 'select',
      label: 'Presupuesto tentativo',
      required: true,
      options: INSTITUTIONAL_BUDGET_OPTIONS,
    },
    {
      name: 'programModality',
      type: 'select',
      label: 'Modalidad del programa',
      required: true,
      options: INSTITUTIONAL_MODALITY_OPTIONS,
    },
    {
      name: 'trainingLocation',
      type: 'select',
      label: 'Región donde se realizaría la capacitación',
      required: true,
      options: REGION_OPTIONS_BY_COUNTRY.cl,
      layout: { span: 2 },
      visibleWhen: {
        field: 'programModality',
        values: INSTITUTIONAL_ONSITE_MODALITIES,
      },
    },
    {
      name: 'numberOfPeople',
      type: 'select',
      label: 'Cantidad de personas',
      options: INSTITUTIONAL_HEADCOUNT_OPTIONS,
      layout: { span: 2 },
    },
    {
      name: 'businessTopicsInterest',
      type: 'textarea',
      label: 'Temas de interés y observaciones',
      required: true,
      placeholder:
        'Ej.: prevención del suicidio para equipos de atención primaria, 20 personas, segundo semestre.',
      layout: { span: 2 },
    },
  ],
  submit: {
    label: 'Enviar solicitud',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Solicitud enviada!',
      message:
        'Recibimos los datos de tu organización. En los próximos días te contactaremos con una propuesta a la medida.',
    },
    error: {
      title: 'No pudimos enviar tu solicitud',
      message: 'Intenta nuevamente en unos segundos.',
    },
    validation: {
      phone: 'Ingresa un teléfono válido.',
      select: 'Selecciona una opción.',
      checkbox: 'Debes aceptar los términos y condiciones para continuar.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'institutional-training-widget',
  },
  scenarioValues: {
    partial: {
      firstname: 'Camila',
      lastname: 'Rojas',
      email: 'camila@municipalidad.cl',
    },
    complete: {
      firstname: 'Camila',
      lastname: 'Rojas',
      email: 'camila@municipalidad.cl',
      company: 'Municipalidad Demo',
      phone: '+56 9 1234 5678',
      tentativeBudgetRange: '1.500.000 a 3.000.000 CLP',
      programModality: 'Online en vivo',
      numberOfPeople: '11_25',
      businessTopicsInterest: 'Primeros auxilios psicológicos para el equipo de atención primaria.',
    },
  },
};

export const giftCourseBaseConfig: FormWidgetConfig = {
  formId: 'gift-course',
  variant: 'gift-course',
  country: 'CO',
  header: {
    title: 'Ingresa tus datos y recibe la información para acceder al curso de regalo',
    align: 'left',
  },
  fields: [
    {
      name: 'giftCourse',
      type: 'select',
      label: 'Elige el curso de regalo',
      required: true,
      placeholder: 'Selecciona un curso',
      options: [],
      layout: { span: 2 },
    },
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombres',
      required: true,
      autocomplete: 'given-name',
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      required: true,
      autocomplete: 'family-name',
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo',
      required: true,
      autocomplete: 'email',
      layout: { span: 2 },
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Número de teléfono',
      required: true,
      layout: { span: 2 },
    },
    {
      name: 'identificationCountry',
      type: 'select',
      label: 'País',
      required: true,
      hidden: true,
      options: COUNTRY_OPTIONS,
    },
    {
      name: 'identificationNumber',
      type: 'text',
      label: 'Cédula',
      placeholder: IDENTIFICATION_CONFIG.CO.placeholder,
      required: true,
      inputMode: IDENTIFICATION_CONFIG.CO.inputMode,
      layout: { span: 2 },
      validation: {
        type: 'identification',
        countryField: 'identificationCountry',
      },
    },
    {
      name: 'educationLevel',
      type: 'select',
      label: 'Nivel de estudios',
      required: true,
      placeholder: 'Selecciona una opción',
      options: EDUCATION_LEVEL_OPTIONS,
    },
    {
      name: 'career',
      type: 'select',
      label: 'Carrera universitaria',
      required: true,
      placeholder: 'Selecciona una opción',
      options: CAREER_OPTIONS,
    },
  ],
  submit: {
    label: 'Enviar',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: 'Formulario enviado',
      message:
        'Recibimos tu solicitud para el curso de regalo. Dentro de las próximas 24 horas busca en tu correo las instrucciones para comenzar.',
    },
    error: {
      title: 'No pudimos enviar tu solicitud',
      message: 'Intenta nuevamente en unos segundos.',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    ...PRIVACY,
  },
  context: {
    source: 'gift-course',
  },
  scenarioValues: {
    partial: {
      firstName: 'Valentina',
      lastName: 'Ramírez',
      email: 'valentina@ejemplo.com',
      identificationCountry: 'CO',
    },
    complete: {
      firstName: 'Valentina',
      lastName: 'Ramírez',
      email: 'valentina@ejemplo.com',
      phone: '+573001234567',
      identificationCountry: 'CO',
      identificationNumber: '1234567890',
      educationLevel: 'Profesional Egresado',
      career: 'Psicología',
    },
  },
};

const JOB_COUNTRY_OPTIONS = [
  { label: 'Chile', value: 'cl' },
  { label: 'México', value: 'mx' },
  { label: 'Colombia', value: 'co' },
  { label: 'Argentina', value: 'ar' },
];

const JOB_AREA_OPTIONS = [
  { label: 'Psicología', value: 'psicologo' },
  { label: 'Salud ocupacional', value: 'salud_ocupacional' },
];

const JOB_SCHEDULE_OPTIONS = [
  { label: 'Jornada completa', value: 'tiempo_completo' },
  { label: 'Media jornada', value: 'medio_tiempo' },
  { label: 'Por horas', value: 'por_horas' },
];

const JOB_WORK_MODE_OPTIONS = [
  { label: 'Presencial', value: 'presencial' },
  { label: 'Remoto', value: 'remoto' },
  { label: 'Híbrido', value: 'hibrido' },
];

export const jobPostingBaseConfig: FormWidgetConfig = {
  formId: 'job-posting',
  variant: 'job-posting',
  country: 'CL',
  header: {
    title: 'Publicar un empleo',
    description:
      'Publica tu oferta en la bolsa de Adipa y llega a profesionales de psicología y salud mental. Es gratis y la revisamos antes de publicarla.',
    align: 'left',
  },
  steps: [
    {
      id: 'oferta',
      title: 'El cargo',
      description: 'Qué puesto necesitas cubrir y qué hará la persona en el día a día.',
      fields: ['cargo', 'area', 'jornada', 'modalidad', 'descripcion'],
    },
    {
      id: 'ubicacion',
      title: 'La ubicación',
      description: 'Dónde queda el puesto. Con esto tu oferta aparece en las búsquedas por ciudad.',
      fields: ['pais', 'region', 'ciudad'],
    },
    {
      id: 'postulacion',
      title: 'La empresa',
      description: 'Quién contrata y a dónde llega quien quiera postular.',
      fields: ['empresa', 'url', 'salario'],
    },
  ],
  fields: [
    {
      name: 'cargo',
      type: 'text',
      label: 'Cargo',
      required: true,
      placeholder: 'Ej. Psicólogo/a clínico/a',
      maxLength: 120,
      layout: { span: 2 },
      requiredMessage: 'Escribe el cargo: es lo primero que se lee en la oferta.',
    },
    {
      name: 'area',
      type: 'select',
      label: 'Área',
      required: true,
      placeholder: 'Selecciona un área',
      options: JOB_AREA_OPTIONS,
      requiredMessage: 'Elige un área para que la oferta llegue a los profesionales indicados.',
    },
    {
      name: 'jornada',
      type: 'select',
      label: 'Jornada',
      placeholder: 'Selecciona una jornada',
      options: JOB_SCHEDULE_OPTIONS,
    },
    {
      name: 'modalidad',
      type: 'select',
      label: 'Modalidad',
      placeholder: 'Selecciona una modalidad',
      options: JOB_WORK_MODE_OPTIONS,
    },
    {
      name: 'descripcion',
      type: 'textarea',
      label: 'Descripción del cargo',
      required: true,
      placeholder: 'Funciones, requisitos y condiciones de la oferta.',
      helperText:
        'Al menos 30 caracteres. Mientras más claro el aviso, mejores postulaciones recibes.',
      minLength: 30,
      maxLength: 5000,
      layout: { span: 2 },
      requiredMessage: 'Cuéntanos qué hará la persona en el cargo.',
    },
    {
      name: 'pais',
      type: 'select',
      label: 'País',
      required: true,
      placeholder: 'Selecciona un país',
      options: JOB_COUNTRY_OPTIONS,
      requiredMessage: 'Elige el país donde se trabaja.',
    },
    {
      name: 'region',
      type: 'select',
      label: 'Región',
      required: true,
      placeholder: 'Selecciona una región',
      optionsWhen: {
        field: 'pais',
        options: REGION_OPTIONS_BY_COUNTRY,
      },
      displayWhen: {
        field: 'pais',
        values: {
          mx: { label: 'Estado', placeholder: 'Selecciona un estado' },
          co: { label: 'Departamento', placeholder: 'Selecciona un departamento' },
          ar: { label: 'Provincia', placeholder: 'Selecciona una provincia' },
        },
      },
      requiredMessage: 'Elige la región donde se trabaja.',
    },
    {
      // Selector y no texto libre: una ciudad escrita a mano ensucia el
      // listado y no sirve para buscar.
      name: 'ciudad',
      type: 'select',
      label: 'Ciudad',
      required: true,
      placeholder: 'Selecciona una ciudad',
      optionsWhen: {
        fields: ['pais', 'region'],
        options: CITY_OPTIONS_BY_REGION,
      },
      requiredMessage: 'Elige la ciudad donde se trabaja.',
    },
    {
      name: 'empresa',
      type: 'text',
      label: 'Empresa',
      required: true,
      placeholder: 'Nombre de la empresa o institución',
      maxLength: 120,
      autocomplete: 'organization',
      layout: { span: 2 },
      requiredMessage: 'Escribe quién contrata: sin eso la oferta no genera confianza.',
    },
    {
      name: 'url',
      type: 'url-or-email',
      label: 'Dónde postular',
      required: true,
      placeholder: 'https://empresa.cl/postular o rrhh@empresa.cl',
      inputMode: 'url',
      helperText:
        'Un enlace a tu aviso o formulario, o el correo donde quieres recibir las postulaciones.',
      maxLength: 1000,
      layout: { span: 2 },
      requiredMessage: 'Falta dónde postular: es la única forma de que te contacten.',
    },
    {
      name: 'salario',
      type: 'select',
      label: 'Banda salarial',
      placeholder: 'Selecciona un rango',
      helperText: 'Opcional. Ayuda a que quien postula sepa si la oferta calza con lo que busca.',
      optionsWhen: {
        field: 'pais',
        options: SALARY_OPTIONS_BY_COUNTRY,
      },
      layout: { span: 2 },
    },
  ],
  submit: {
    label: 'Enviar mi oferta',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Los campos marcados con * son obligatorios.',
    success: {
      title: '¡Recibimos tu oferta!',
      message:
        'La revisamos antes de publicarla en la bolsa de empleos. Si todo está en orden, aparecerá en el listado dentro de los próximos días hábiles.',
    },
    error: {
      title: 'No pudimos enviar tu oferta.',
      message: 'Puede ser algo momentáneo. Intenta de nuevo en unos segundos.',
    },
    validation: {
      select: 'Elige una opción para continuar.',
      checkbox: 'Confirma que la oferta es real para poder publicarla.',
      urlOrEmail:
        'Revisa el dato: tiene que ser un enlace (empresa.cl/postular) o un correo (rrhh@empresa.cl).',
    },
  },
  privacy: {
    enabled: true,
    required: true,
    label: 'Confirmo que la oferta es real y acepto los',
    linkLabel: 'términos y condiciones',
  },
  context: {
    source: 'job-posting-widget',
  },
  scenarioValues: {
    complete: {
      cargo: 'Psicólogo/a clínico/a',
      area: 'psicologo',
      jornada: 'tiempo_completo',
      modalidad: 'hibrido',
      descripcion:
        'Atención clínica de adultos en modalidad híbrida, con supervisión de casos y trabajo en equipo interdisciplinario.',
      pais: 'cl',
      region: 'Metropolitana de Santiago',
      ciudad: 'Santiago',
      empresa: 'Centro de Salud Mental Demo',
      url: 'https://centrodemo.cl/postular',
      salario: '$1.500.000 – $2.000.000',
    },
  },
};


const SITE_URL_BY_COUNTRY: Record<FormWidgetCountry, string> = {
  AR: 'adipa.ar',
  CL: 'adipa.cl',
  MX: 'adipa.mx',
  CO: 'adipa.co',
};

const REGISTER_COUNTRY_HELPER = 'El país de tu documento de identidad.';

function createRegisterCountryHelpers(siteCountry: FormWidgetCountry) {
  const helpers: Record<string, FormFieldDisplayOverride> = {};

  SUPPORTED_FORM_COUNTRIES.forEach((country) => {
    if (country === siteCountry) return;
    helpers[country] = {
      helperText: `¿Tu documento es de ${FORM_COUNTRY_LABELS[country]}? Regístrate en {link} para comprar en tu moneda. También puedes seguir aquí.`,
      helperLink: {
        href: `https://${SITE_URL_BY_COUNTRY[country]}/register`,
        label: SITE_URL_BY_COUNTRY[country],
      },
    };
  });

  return helpers;
}

function createIdentificationDisplay() {
  return Object.fromEntries(
    (Object.keys(IDENTIFICATION_CONFIG) as (keyof typeof IDENTIFICATION_CONFIG)[]).map((country) => [
      country,
      {
        label: IDENTIFICATION_CONFIG[country].label,
        placeholder: IDENTIFICATION_CONFIG[country].placeholder,
        inputMode: IDENTIFICATION_CONFIG[country].inputMode,
      },
    ]),
  );
}

/**
 * Alta de cuenta en dos pasos: lo justo para crear la cuenta rápido. Mismos
 * campos, validaciones y nombres que el perfil, para que el checkout no vuelva
 * a pedir lo que el registro dejó bien; la formación se completa en el perfil.
 * El país se explica antes de pedir el documento porque las bases legacy
 * quedaron llenas de RFC guardados como RUT y de residentes que eligieron
 * "Otro" leyendo país como nacionalidad.
 */
export function createRegisterConfig(country: FormWidgetCountry = 'CL'): FormWidgetConfig {
  const identification = IDENTIFICATION_CONFIG[country];

  return {
    formId: 'register',
    variant: 'register',
    country,
    header: {
      title: 'Crea tu cuenta en ADIPA',
      description:
        'Con una sola cuenta accedes a tus programas de estudio, tus certificados y el aula virtual.',
      align: 'left',
    },
    steps: [
      {
        id: 'account',
        title: 'Tu cuenta',
        description: 'Con este correo y contraseña entras a ADIPA y al aula virtual.',
        fields: ['email', 'password', 'passwordConfirmation'],
      },
      {
        id: 'identity',
        title: 'Tus datos',
        description:
          'Aparecen en tus certificados y sirven para verificar tu identidad: escríbelos tal como están en tu documento.',
        fields: [
          'firstName',
          'lastName',
          'birthDate',
          'identificationCountry',
          'identificationNumber',
          'phone',
        ],
      },
      {
        id: 'education',
        title: 'Tu formación',
        description: 'Con esto te recomendamos programas de tu área.',
        fields: ['educationLevel', 'career', 'practiceArea'],
      },
    ],
    fields: [
      {
        name: 'email',
        type: 'email',
        label: 'Email',
        required: true,
        autocomplete: 'email',
        placeholder: 'tu@email.com',
        icon: 'mail',
        layout: { span: 2 },
      },
      {
        name: 'password',
        type: 'password',
        label: 'Contraseña',
        required: true,
        autocomplete: 'new-password',
        password: { rules: true },
        icon: 'lock',
        layout: { span: 2 },
      },
      {
        name: 'passwordConfirmation',
        type: 'password',
        label: 'Repite la contraseña',
        required: true,
        autocomplete: 'new-password',
        password: { matchField: 'password' },
        icon: 'lock',
        layout: { span: 2 },
      },
      {
        name: 'firstName',
        type: 'text',
        label: 'Nombres',
        required: true,
        autocomplete: 'given-name',
        minLength: 2,
        maxLength: 100,
        icon: 'user',
      },
      {
        name: 'lastName',
        type: 'text',
        label: 'Apellidos',
        required: true,
        autocomplete: 'family-name',
        minLength: 2,
        maxLength: 100,
        icon: 'user',
      },
      {
        name: 'birthDate',
        type: 'date',
        label: 'Fecha de nacimiento',
        required: true,
        placeholder: 'Elige tu fecha',
        helperText: 'Para verificar tu identidad. Debes tener al menos 18 años.',
        date: {
          minAge: 18,
          maxAge: 110,
          message: 'Para crear una cuenta debes tener al menos 18 años.',
        },
        layout: { span: 2 },
      },
      {
        name: 'identificationCountry',
        type: 'select',
        label: 'País',
        required: true,
        defaultValue: country,
        options: COUNTRY_OPTIONS,
        helperText: REGISTER_COUNTRY_HELPER,
        displayWhen: {
          field: 'identificationCountry',
          values: createRegisterCountryHelpers(country),
        },
        layout: { span: 2 },
      },
      {
        name: 'identificationNumber',
        type: 'text',
        label: identification.label,
        placeholder: identification.placeholder,
        required: true,
        inputMode: identification.inputMode,
        infoText: 'Aparece en tus certificados: escríbelo tal como está en tu documento.',
        icon: 'id',
        layout: { span: 2 },
        displayWhen: {
          field: 'identificationCountry',
          values: createIdentificationDisplay(),
        },
        validation: {
          type: 'identification',
          countryField: 'identificationCountry',
        },
      },
      {
        name: 'phone',
        type: 'phone',
        label: 'Teléfono',
        required: true,
        helperText: 'Solo el número. Te contactamos por temas de tus programas.',
        layout: { span: 2 },
      },
      {
        name: 'educationLevel',
        type: 'select',
        label: 'Nivel de educación',
        required: true,
        placeholder: 'Selecciona una opción',
        options: EDUCATION_LEVEL_OPTIONS,
        layout: { span: 2 },
      },
      {
        name: 'career',
        type: 'select',
        label: 'Carrera',
        placeholder: 'Selecciona una opción',
        options: CAREER_OPTIONS,
        layout: { span: 2 },
        visibleWhen: {
          field: 'educationLevel',
          values: EDUCATION_LEVELS_WITH_CAREER,
        },
      },
      {
        ...practiceAreaField,
        required: false,
      },
    ],
    submit: {
      label: 'Crear mi cuenta',
      align: 'full',
    },
    messages: {
      requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
      success: {
        title: '¡Tu cuenta está lista!',
        message: 'Ahora inicia sesión con tu correo y contraseña para entrar a ADIPA.',
      },
      error: {
        title: 'No pudimos crear tu cuenta.',
        message: 'Revisa los datos e intenta nuevamente en unos segundos.',
      },
      validation: {
        required: 'Completa este campo para continuar.',
      },
    },
    privacy: {
      enabled: true,
      required: true,
      ...PRIVACY,
    },
    context: {
      source: 'register-widget',
      platformCountry: country,
      platformCountryLabel: FORM_COUNTRY_LABELS[country],
    },
    scenarioValues: {
      partial: {
        email: 'camila@ejemplo.com',
        firstName: 'Camila',
        lastName: 'Muñoz',
        identificationCountry: country,
      },
      complete: {
        email: 'camila@ejemplo.com',
        password: 'Adipa2026!',
        passwordConfirmation: 'Adipa2026!',
        firstName: 'Camila',
        lastName: 'Muñoz',
        birthDate: '1994-06-15',
        identificationCountry: country,
        identificationNumber: identification.placeholder,
        phone: '+56912345678',
        educationLevel: 'Profesional Egresado',
        career: 'Psicología',
        practiceArea: 'Psicología Clínica',
      },
    },
  };
}

/**
 * Recuperación de contraseña, paso 1: solo el correo. La respuesta es la misma
 * exista o no la cuenta, para no confirmar correos ajenos.
 */
export function createForgotPasswordConfig(country: FormWidgetCountry = 'CL'): FormWidgetConfig {
  return {
    formId: 'forgot-password',
    variant: 'forgot-password',
    country,
    header: {
      title: 'Recupera tu contraseña',
      description:
        'Escribe el correo de tu cuenta y te enviaremos un enlace para crear una contraseña nueva.',
      align: 'center',
    },
    fields: [
      {
        name: 'email',
        type: 'email',
        label: 'Email',
        required: true,
        autocomplete: 'email',
        placeholder: 'tu@email.com',
        icon: 'mail',
        layout: { span: 2 },
      },
    ],
    submit: {
      label: 'Enviarme el enlace',
      align: 'full',
    },
    messages: {
      success: {
        title: 'Revisa tu correo',
        message:
          'Si ese correo tiene una cuenta en ADIPA, te llegará un enlace para crear tu contraseña nueva. Vale por 60 minutos; si no lo ves, mira en spam.',
      },
      error: {
        title: 'No pudimos enviar el enlace.',
        message: 'Intenta nuevamente en unos segundos.',
      },
      validation: {
        required: 'Escribe el correo de tu cuenta.',
      },
    },
    privacy: {
      enabled: false,
      required: false,
      ...PRIVACY,
    },
    context: {
      source: 'forgot-password-widget',
      platformCountry: country,
      platformCountryLabel: FORM_COUNTRY_LABELS[country],
    },
    scenarioValues: {
      partial: { email: 'camila@ejemplo.com' },
      complete: { email: 'camila@ejemplo.com' },
    },
  };
}

/**
 * Recuperación de contraseña, paso 2: la contraseña nueva. El token y el
 * correo del enlace los inyecta el host por `extraFields`; la persona no los
 * ve ni los escribe.
 */
export function createResetPasswordConfig(country: FormWidgetCountry = 'CL'): FormWidgetConfig {
  return {
    formId: 'reset-password',
    variant: 'reset-password',
    country,
    header: {
      title: 'Crea tu contraseña nueva',
      description: 'Elige una contraseña que no uses en otro sitio.',
      align: 'center',
    },
    fields: [
      {
        name: 'password',
        type: 'password',
        label: 'Contraseña nueva',
        required: true,
        autocomplete: 'new-password',
        password: { rules: true },
        icon: 'lock',
        layout: { span: 2 },
      },
      {
        name: 'passwordConfirmation',
        type: 'password',
        label: 'Repite la contraseña',
        required: true,
        autocomplete: 'new-password',
        password: { matchField: 'password' },
        icon: 'lock',
        layout: { span: 2 },
      },
    ],
    submit: {
      label: 'Guardar mi contraseña',
      align: 'full',
    },
    messages: {
      success: {
        title: '¡Contraseña actualizada!',
        message: 'Ya puedes iniciar sesión con tu contraseña nueva.',
      },
      error: {
        title: 'No pudimos guardar la contraseña.',
        message: 'Intenta nuevamente en unos segundos.',
      },
      validation: {
        required: 'Completa este campo para continuar.',
      },
    },
    privacy: {
      enabled: false,
      required: false,
      ...PRIVACY,
    },
    context: {
      source: 'reset-password-widget',
      platformCountry: country,
      platformCountryLabel: FORM_COUNTRY_LABELS[country],
    },
    scenarioValues: {
      partial: {},
      complete: { password: 'Adipa2026!', passwordConfirmation: 'Adipa2026!' },
    },
  };
}

const LITERARY_CONTEST_STORY_MAX_BYTES = 4 * 1024 * 1024;

const LITERARY_CONTEST_STORY_ACCEPT = [
  'application/pdf',
  '.pdf',
  'application/msword',
  '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.docx',
].join(',');

const LITERARY_CONTEST_AUTHORSHIP_OPTIONS = [
  { label: 'Con mi nombre real', value: 'real' },
  { label: 'Con un seudónimo', value: 'pseudonym' },
];

const LITERARY_CONTEST_EDUCATION_LEVEL_OPTIONS = [
  { label: 'Estudiante de pregrado', value: 'undergraduate' },
  { label: 'Profesional egresado', value: 'graduate' },
  { label: 'Técnico profesional', value: 'technical' },
  { label: 'Posgrado', value: 'postgraduate' },
  { label: 'Otro', value: 'other' },
];

const LITERARY_CONTEST_PRACTICE_AREA_OPTIONS = [
  { label: 'Psicología', value: 'psychology' },
  { label: 'Terapia ocupacional', value: 'occupational-therapy' },
  { label: 'Trabajo social', value: 'social-work' },
  { label: 'Educación y pedagogía', value: 'education' },
  { label: 'Administración pública', value: 'public-administration' },
  { label: 'Ciencias sociales', value: 'social-sciences' },
  { label: 'Derecho y/o ciencias jurídicas', value: 'law' },
  { label: 'Enfermería', value: 'nursing' },
  { label: 'Fonoaudiología', value: 'speech-therapy' },
  { label: 'Kinesiología', value: 'physiotherapy' },
  { label: 'Medicina', value: 'medicine' },
  { label: 'Nutrición', value: 'nutrition' },
  { label: 'Obstetricia y puericultura', value: 'obstetrics' },
  { label: 'Sociología', value: 'sociology' },
  { label: 'Otra', value: 'other' },
];

export const literaryContestBaseConfig: FormWidgetConfig = {
  formId: 'literary-contest',
  variant: 'literary-contest',
  country: 'CL',
  header: {
    title: 'Envía tu cuento en dos pasos',
    description:
      'Primero tus datos de contacto, después el cuento y tu firma. Puedes volver atrás sin perder nada.',
    align: 'left',
  },
  steps: [
    {
      id: 'author',
      title: 'Tus datos',
      description:
        'Sirven para confirmar tu envío y avisarte si tu cuento gana. El jurado no los ve: cada cuento se lee con un código.',
      fields: [
        'firstName',
        'lastName',
        'email',
        'phone',
        'birthDate',
        'educationLevel',
        'educationLevelOther',
        'practiceArea',
        'practiceAreaOther',
      ],
    },
    {
      id: 'story',
      title: 'Tu cuento',
      description: 'Elige cómo firmarlo, adjunta el archivo y firma las bases para enviarlo.',
      fields: ['authorship', 'pseudonym', 'story', 'sensitiveContent', 'termsSignature'],
    },
  ],
  fields: [
    {
      name: 'firstName',
      type: 'text',
      label: 'Nombre',
      placeholder: 'Tu nombre',
      required: true,
      requiredMessage: 'Escribe tu nombre.',
      minLength: 2,
      maxLength: 80,
      autocomplete: 'given-name',
      layout: { span: 1 },
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Apellidos',
      placeholder: 'Tus apellidos',
      required: true,
      requiredMessage: 'Escribe tus apellidos.',
      minLength: 2,
      maxLength: 80,
      autocomplete: 'family-name',
      layout: { span: 1 },
    },
    {
      name: 'email',
      type: 'email',
      label: 'Correo electrónico',
      placeholder: 'tucorreo@ejemplo.com',
      required: true,
      autocomplete: 'email',
      layout: { span: 1 },
    },
    {
      name: 'phone',
      type: 'phone',
      label: 'Teléfono de contacto',
      required: true,
      layout: { span: 1 },
    },
    {
      name: 'birthDate',
      type: 'date',
      label: 'Fecha de nacimiento',
      required: true,
      requiredMessage: 'Elige tu fecha de nacimiento.',
      placeholder: 'Elige tu fecha',
      date: {
        maxAge: 110,
        message: 'Revisa la fecha: no parece una fecha de nacimiento.',
      },
    },
    {
      name: 'authorship',
      type: 'select',
      label: 'Si tu cuento se publica, ¿cómo quieres firmarlo?',
      helperText:
        'Así firmaremos tu cuento si se publica; tus datos personales siguen siendo confidenciales.',
      placeholder: 'Elige una opción',
      required: true,
      requiredMessage: 'Elige cómo quieres firmar tu cuento.',
      options: LITERARY_CONTEST_AUTHORSHIP_OPTIONS,
    },
    {
      name: 'pseudonym',
      type: 'text',
      label: 'Tu seudónimo',
      helperText: 'Escríbelo tal como quieres que aparezca publicado.',
      placeholder: 'Seudónimo',
      required: true,
      requiredMessage: 'Escribe el seudónimo con el que firmarás.',
      maxLength: 80,
      visibleWhen: { field: 'authorship', values: ['pseudonym'] },
    },
    {
      name: 'educationLevel',
      type: 'select',
      label: 'Nivel de estudios',
      helperText: 'Selecciona el último nivel que completaste.',
      placeholder: 'Elige una opción',
      required: true,
      requiredMessage: 'Selecciona tu nivel de estudios.',
      options: LITERARY_CONTEST_EDUCATION_LEVEL_OPTIONS,
    },
    {
      name: 'educationLevelOther',
      type: 'text',
      label: '¿Cuál?',
      placeholder: 'Cuéntanos tu nivel de estudios',
      required: true,
      requiredMessage: 'Cuéntanos cuál es tu nivel de estudios.',
      maxLength: 120,
      visibleWhen: { field: 'educationLevel', values: ['other'] },
    },
    {
      name: 'practiceArea',
      type: 'select',
      label: '¿En cuál de estas áreas te desempeñas?',
      helperText: 'Elige la opción que mejor describe tu ámbito de acción.',
      placeholder: 'Elige una opción',
      required: true,
      requiredMessage: 'Selecciona el área en la que te desempeñas.',
      options: LITERARY_CONTEST_PRACTICE_AREA_OPTIONS,
    },
    {
      name: 'practiceAreaOther',
      type: 'text',
      label: '¿Cuál?',
      placeholder: 'Cuéntanos en qué área te desempeñas',
      required: true,
      requiredMessage: 'Cuéntanos en qué área te desempeñas.',
      maxLength: 120,
      visibleWhen: { field: 'practiceArea', values: ['other'] },
    },
    {
      name: 'story',
      type: 'file',
      label: 'Tu cuento en PDF o Word',
      placeholder: 'elígelo desde tu equipo',
      helperText:
        'Hasta 250 palabras, sin tu nombre ni datos personales en el texto ni en el nombre del archivo.',
      required: true,
      requiredMessage: 'Adjunta tu cuento en PDF o Word.',
      file: {
        accept: LITERARY_CONTEST_STORY_ACCEPT,
        maxBytes: LITERARY_CONTEST_STORY_MAX_BYTES,
        typeMessage: 'El archivo debe ser PDF o Word (.pdf, .doc o .docx).',
        sizeMessage: 'El archivo no puede pesar más de 4 MB.',
      },
    },
    {
      name: 'sensitiveContent',
      type: 'checkbox',
      label: 'Mi cuento aborda temas sensibles que podrían afectar a algunas personas lectoras.',
    },
    {
      name: 'termsSignature',
      type: 'signature',
      label: 'He leído y estoy de acuerdo con las bases del concurso',
      helperText: 'Firma dibujando o escribiendo tu nombre. Antes de firmar, revisa {link}.',
      helperLink: { href: '#bases', label: 'las bases del concurso' },
      required: true,
      requiredMessage: 'Firma para confirmar que aceptas las bases.',
      file: {
        accept: 'image/png',
        maxBytes: 200 * 1024,
        typeMessage: 'No pudimos guardar tu firma. Bórrala y vuelve a firmar.',
        sizeMessage: 'Tu firma quedó muy pesada. Bórrala y vuelve a firmar con un trazo más simple.',
      },
    },
  ],
  submit: {
    label: 'Enviar mi cuento',
    align: 'full',
  },
  messages: {
    requiredFieldsLabel: 'Todos los campos marcados con * son obligatorios.',
    success: {
      title: '¡Recibimos tu cuento!',
      message: 'Te enviamos la confirmación a tu correo. Si tu cuento gana, te avisaremos por ahí.',
    },
    error: {
      title: 'No pudimos enviar tu cuento',
      message: 'Revisa tu conexión e inténtalo de nuevo; tus datos siguen acá.',
    },
  },
  privacy: {
    enabled: false,
    required: false,
    ...PRIVACY,
  },
  context: {
    source: 'literary-contest-widget',
  },
};

export const registerBaseConfig = createRegisterConfig('CL');
export const forgotPasswordBaseConfig = createForgotPasswordConfig('CL');
export const resetPasswordBaseConfig = createResetPasswordConfig('CL');

export const defaultFormConfigs = {
  contact: contactBaseConfig,
  partnership: partnershipBaseConfig,
  'institutional-training': institutionalTrainingBaseConfig,
  'gift-course': giftCourseBaseConfig,
  'course-closed': courseClosedBaseConfig,
  'price-reminder': priceReminderBaseConfig,
  'seminar-registration': seminarRegistrationBaseConfig,
  'seminar-attendance': seminarAttendanceBaseConfig,
  'resource-download': resourceDownloadBaseConfig,
  'job-posting': jobPostingBaseConfig,
  'literary-contest': literaryContestBaseConfig,
  register: registerBaseConfig,
  'forgot-password': forgotPasswordBaseConfig,
  'reset-password': resetPasswordBaseConfig,
} as const;

function createCountryProgramName(country: FormWidgetCountry) {
  return `Programa ADIPA ${FORM_COUNTRY_LABELS[country]}`;
}

function createCountryConfig(
  config: FormWidgetConfig,
  country: FormWidgetCountry,
): FormWidgetConfig {
  const programName = createCountryProgramName(country);

  return {
    ...config,
    country,
    fields: config.fields.map((field) => {
      if (field.name === 'programName') {
        return {
          ...field,
          defaultValue: programName,
        };
      }

      if (field.name === 'country' || field.name === 'identificationCountry') {
        return {
          ...field,
          defaultValue: country,
        };
      }

      return field;
    }),
    context: {
      ...config.context,
      platformCountry: country,
      platformCountryLabel: FORM_COUNTRY_LABELS[country],
      ...(config.context?.programName ? { programName } : {}),
    },
    scenarioValues: config.scenarioValues
      ? (Object.fromEntries(
          Object.entries(config.scenarioValues).map(([scenario, values]) => [
            scenario,
            {
              ...values,
              ...(values.programName ? { programName } : {}),
              ...(values.country ? { country } : {}),
              ...(values.identificationCountry ? { identificationCountry: country } : {}),
            },
          ]),
        ) as FormWidgetConfig['scenarioValues'])
      : undefined,
  };
}

function createCountryFormConfigs(
  country: FormWidgetCountry,
): Partial<Record<FormWidgetVariant, FormWidgetConfig>> {
  return {
    contact: createCountryConfig(contactBaseConfig, country),
    'course-closed': createCountryConfig(courseClosedBaseConfig, country),
    'price-reminder': createCountryConfig(priceReminderBaseConfig, country),
    'seminar-registration': createCountryConfig(seminarRegistrationBaseConfig, country),
    'seminar-attendance': createCountryConfig(seminarAttendanceBaseConfig, country),
    'resource-download': createCountryConfig(resourceDownloadBaseConfig, country),
    partnership: createCountryConfig(partnershipBaseConfig, country),
    'institutional-training': createCountryConfig(institutionalTrainingBaseConfig, country),
    'gift-course': createCountryConfig(giftCourseBaseConfig, country),
    'job-posting': createCountryConfig(jobPostingBaseConfig, country),
    'literary-contest': createCountryConfig(literaryContestBaseConfig, country),
    register: createRegisterConfig(country),
    'forgot-password': createForgotPasswordConfig(country),
    'reset-password': createResetPasswordConfig(country),
  };
}

export const formConfigs = Object.fromEntries(
  SUPPORTED_FORM_COUNTRIES.map((country) => [country, createCountryFormConfigs(country)]),
) as Record<FormWidgetCountry, Partial<Record<FormWidgetVariant, FormWidgetConfig>>>;

export function getFormConfig(
  variant: FormWidgetVariant,
  country: FormWidgetCountry = 'CL',
): FormWidgetConfig | undefined {
  return formConfigs[country]?.[variant];
}
